import React, { useEffect } from 'react'
import { Select, Tag } from 'antd'
import { useDispatch, useSelector } from 'react-redux'
import { setCurrentChannel } from '../Actions/Channel'

const { Option } = Select

const EMPTY_CHANNELS = []

// Global Channel Selector (FR-2): lives in the header on every page, shows the
// active channel, and lets the merchant switch which channel's settings they're
// editing (US-004). Switching reloads the page - the simplest way to guarantee
// every one of the app's many independent data fetches (locations, carriers,
// addons, shipping rules, RAD settings, etc., all fired from App.js on load)
// picks up the new channel, without refactoring that fetch list in this phase.
const ChannelSelector = () => {
	const dispatch = useDispatch()
	const channels = useSelector(state => state.channels) || EMPTY_CHANNELS
	const currentChannelId = useSelector(state => state.currentChannelId)
	const token = useSelector(state => state.token)

	useEffect(() => {
		if (!currentChannelId && channels.length) {
			const defaultChannel = channels.find(c => c.is_default) || channels[0]
			if (defaultChannel) {
				dispatch(setCurrentChannel(defaultChannel.bc_channel_id))
			}
		}
	}, [channels, currentChannelId, dispatch])

	if (!channels.length) {
		return null
	}

	const activeChannel = channels.find(
		c => c.bc_channel_id === currentChannelId
	)

	const handleChange = value => {
		if (value === currentChannelId) {
			return
		}
		dispatch(setCurrentChannel(value))
		// Not window.location.reload(): that re-requests whatever path the merchant is
		// currently on (e.g. /shipping_rules) as a real HTTP request, which 404s on
		// servers with no SPA catch-all rewrite for anything but the root path.
		// Not window.location.search either: React Router's in-app navigation (the
		// sidebar links) drops the ?store= query param from the address bar the
		// moment the merchant visits any page, even though the app keeps working off
		// the token already sitting in Redux - rebuilding the URL from an empty
		// window.location.search reloads with no token at all. Redux is the only
		// place still guaranteed to have it.
		const target = token
			? `${window.location.origin}/?store=${encodeURIComponent(token)}`
			: `${window.location.origin}/${window.location.search}`
		window.location.href = target
	}

	return (
		<div
			style={{
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'flex-end',
				width: '100%',
				height: '100%',
				padding: '0 24px',
				gap: 12,
				// The legacy .app-logo bar is position:absolute with z-index:300 (and
				// .sidemenu goes to 999 on mobile) and happens to overlap this same
				// header region - without this, the selector renders but sits
				// invisibly underneath it.
				position: 'relative',
				zIndex: 1000,
			}}
		>
			<Tag
				color='blue'
				style={{
					// Matches antd's default Select height (32px) so the two sit flush
					// at the same visual size instead of the Tag looking small next to it.
					height: 32,
					display: 'inline-flex',
					alignItems: 'center',
					fontSize: 16,
					padding: '0 16px',
					margin: 0,
				}}
			>
				Channels: {activeChannel ? activeChannel.channel_name : '...'}
			</Tag>
			<Select
				value={currentChannelId || undefined}
				style={{ minWidth: 200 }}
				onChange={handleChange}
			>
				{channels.map(channel => (
					<Option key={channel.bc_channel_id} value={channel.bc_channel_id}>
						{channel.channel_name}
					</Option>
				))}
			</Select>
		</div>
	)
}

export default ChannelSelector
